import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';

import bind from '../bind';
import CsrfForm from '../csrf_form';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { formatInternationalizedSeconds } from '../date_utils';
import { IUserContext, withUserContext } from './user_context_provider';
import { isOwner } from '../user_context_helpers';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { WEB } from '@reverbdotcom/commons/src/constants';

import {
  AddToCartButtonData,
  mParticleListingFields,
  core_apimessages_PreorderInfo as PreorderInfo,
  core_apimessages_Listing_ListingType as ListingType,
} from '../rql';

import {
  MParticleEventName,
  ProductActionType,
  trackEvent,
} from '../elog/mparticle_tracker';
import { trackCriteoAddToCart } from '../criteo';
import { withRouter, WithRouterProps } from 'react-router';
import { resolveCurrentView } from '../event_tracking/favorites';

interface IListing extends mParticleListingFields.Fragment, AddToCartButtonData.Fragment {
  [x: string]: any;
}

interface IProps extends IUserContext {
  listing?: IListing;
  quantity?: number;
  className?: string;
  disabled?: boolean;
  listingClickEvent?: MParticleEvent;
  position?: number;
  parentComponentName?: string;
  redirectPath?: string;
}

export const AddToCartButtonFragment = gql`
  fragment AddToCartButtonData on Listing {
    _id
    id
    sellerId
    preorderInfo {
      onPreorder
      estimatedShipDate {
        seconds
      }
    }
  }
`;

const DATE_FORMAT = 'LL';

function t(key: string, opt = {}): string {
  return I18n.t(`commons.addToCartButton.${key}`, opt);
}

function isFree(listing): boolean {
  return listing?.pricing?.buyerPrice?.amountCents === 0;
}

function isDigitalListing(listing): boolean {
  return listing?.listingType === ListingType.DIGITAL;
}

export class AddToCartButton extends React.Component<IProps & WithRouterProps, null> {

  @bind
  handleClick(e) {
    trackEvent({
      eventName: MParticleEventName.AddToCart,
      productActionType: ProductActionType.AddToCart,
      listing: this.props.listing,
      position: this.props.position,
      componentName: this.props.parentComponentName,
      currentView: resolveCurrentView(this.props.location),
    });

    trackCriteoAddToCart({
      id: this.props.listing.id,
      price: this.props.listing.pricing.buyerPrice.amount,
      quantity: this.props.quantity || 1,
    }, this.props.user);

    e.stopPropagation();
  }

  get listingId(): string {
    return (this.props.listing && this.props.listing.id) || '';
  }

  get preorder(): PreorderInfo {
    if (!this.props.listing.preorderInfo) {
      return null;
    }

    return this.props.listing.preorderInfo;
  }

  get onPreorder(): boolean {
    if (!this.preorder) {
      return false;
    }

    return this.preorder.onPreorder;
  }

  get title(): string {
    return this.onPreorder ? t('preorder') : t('addToCart');
  }

  get preorderEstimatedShipDate() {
    if (!this.preorder || !this.onPreorder) {
      return null;
    }

    const { seconds } = this.preorder.estimatedShipDate;

    return (
      <div className="add-to-cart-button__preorder-date">
        {t('preorderEstimatedShipDate', {
          date: formatInternationalizedSeconds(seconds, DATE_FORMAT),
        })}
      </div>
    );
  }

  get showDisabled(): boolean {
    const { user, listing } = this.props;

    return this.props.disabled || isOwner(user, listing);
  }

  render() {
    const { className, listing, listingClickEvent, user } = this.props;
    const classes = classNames(
      'add-to-cart-button',
      className,
    );

    if (isFree(listing) && isDigitalListing(listing)) {
      return (
        <CoreLink
          className={classes}
          to={listingItemPagePath(listing)}
          target={user?.deviceName === WEB ? '_blank' : ''}
          clickEvent={listingClickEvent}
        >
          {I18n.t('commons.gridCardRow.viewListing')}
        </CoreLink>
      );
    }

    if (this.showDisabled) {
      return (
        <>
          <div
            className={classes}
            data-disabled
          >
            {this.title}
          </div>
          {
            this.onPreorder &&
            this.preorderEstimatedShipDate
          }
        </>
      );
    }

    return (
      <CsrfForm
        noValidate
        id={`new_cart_item-${this.listingId}`}
        action="/cart"
        acceptCharset="UTF-8"
        method="post"
        key={this.listingId}
      >
        <input
          value="web"
          type="hidden"
          name="cart_item[action_source_attributes][device]"
          id={`cart_item_action_source_attributes_device-${this.listingId}`}
        />
        <input
          value={this.listingId}
          type="hidden"
          name="cart_item[product_id]"
          id={`cart_item_product_id-${this.listingId}`}
        />
        {this.props.listing?.bumpKey?.key && <input
          type="hidden"
          name="cart_item[bump_key]"
          id={`cart_item_bump_key-${this.listingId}`}
          value={this.props.listing.bumpKey.key}
        />}
        <input
          type="hidden"
          name="cart_item[quantity]"
          id={`cart_item_for_product-${this.listingId}_quantity-${this.props.quantity}`}
          value={this.props.quantity || 1}
        />

        {this.props.redirectPath && (
          <input
            type="hidden"
            name="cart_item[redirect_path]"
            id={`cart_item_redirect_path-${this.listingId}`}
            value={this.props.redirectPath}
          />
        )}

        <button
          type="submit"
          className={classes}
          onClick={this.handleClick}
        >
          {this.title}
        </button>
        {this.preorderEstimatedShipDate}
      </CsrfForm>
    );
  }
}

export default withRouter(withUserContext(AddToCartButton));
